import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from 'react';
import { Box } from '@mui/material';
import {
  TInventoryItem,
  TInventoryTransaction,
  TInventoryCategory,
  TInventoryItemListApiResponse,
  TInventoryTransactionListApiResponse,
  TInventoryCategoryListApiResponse,
} from 'src/apis/inventory/inventory.api.interfaces';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useInventoryItemListHook } from '../hooks/useInventoryItemHooks';
import { useInventoryTransactionListHook } from '../hooks/useInventoryTransactionHooks';
import { useInventoryCategoryListHook } from '../hooks/useInventoryCategoryHooks';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';

type TEditMode = 'create' | 'edit' | 'view';

export enum EViewState {
  LIST = 'list',
  DETAILS = 'details',
  EDIT = 'edit',
  ADD = 'add',
}

export enum ACTION_TYPE {
  SET_ITEMS = 'SET_ITEMS',
  SET_TRANSACTIONS = 'SET_TRANSACTIONS',
  SET_CATEGORIES = 'SET_CATEGORIES',
  SELECT_ITEM = 'SELECT_ITEM',
  SELECT_TRANSACTION = 'SELECT_TRANSACTION',
  SELECT_CATEGORY = 'SELECT_CATEGORY',
  ADD_ITEM = 'ADD_ITEM',
  UPDATE_ITEM = 'UPDATE_ITEM',
  DELETE_ITEM = 'DELETE_ITEM',
  ADD_TRANSACTION = 'ADD_TRANSACTION',
  UPDATE_TRANSACTION = 'UPDATE_TRANSACTION',
  DELETE_TRANSACTION = 'DELETE_TRANSACTION',
  ADD_CATEGORY = 'ADD_CATEGORY',
  UPDATE_CATEGORY = 'UPDATE_CATEGORY',
  DELETE_CATEGORY = 'DELETE_CATEGORY',
  SET_SEARCH_TERM = 'SET_SEARCH_TERM',
  SET_CATEGORY_FILTER = 'SET_CATEGORY_FILTER',
  FILTER_ITEMS = 'FILTER_ITEMS',
  SET_LOADING = 'SET_LOADING',
  SET_ERROR = 'SET_ERROR',
  SET_EDIT_MODE = 'SET_EDIT_MODE',
  SET_VIEW_STATE = 'SET_VIEW_STATE',
  SET_VIEW_TYPE = 'SET_VIEW_TYPE',
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
  SET_MODAL_OPEN = 'SET_MODAL_OPEN',
}

export type TInventoryViewType = 'items' | 'categories' | 'transactions';

type TInventoryState = {
  items: TInventoryItem[];
  transactions: TInventoryTransaction[];
  categories: TInventoryCategory[];
  selectedItem?: TInventoryItem;
  selectedTransaction?: TInventoryTransaction;
  selectedCategory?: TInventoryCategory;
  filteredItems: TInventoryItem[];
  searchTerm: string;
  selectedCategoryFilter: string;
  isLoading: boolean;
  errorMessage?: string;
  editMode?: TEditMode;
  viewState: EViewState;
  currentViewType?: TInventoryViewType;
  paginationParams: TPaginationParams;
  itemsCount: number;
  transactionsCount: number;
  categoriesCount: number;
  isModalOpen: boolean;
};

type Action =
  | { type: ACTION_TYPE.SET_ITEMS; payload: TInventoryItemListApiResponse }
  | {
      type: ACTION_TYPE.SET_TRANSACTIONS;
      payload: TInventoryTransactionListApiResponse;
    }
  | {
      type: ACTION_TYPE.SET_CATEGORIES;
      payload: TInventoryCategoryListApiResponse;
    }
  | { type: ACTION_TYPE.SELECT_ITEM; payload: TInventoryItem | undefined }
  | {
      type: ACTION_TYPE.SELECT_TRANSACTION;
      payload: TInventoryTransaction | undefined;
    }
  | {
      type: ACTION_TYPE.SELECT_CATEGORY;
      payload: TInventoryCategory | undefined;
    }
  | { type: ACTION_TYPE.ADD_ITEM; payload: TInventoryItem }
  | { type: ACTION_TYPE.UPDATE_ITEM; payload: TInventoryItem }
  | { type: ACTION_TYPE.DELETE_ITEM; payload: string }
  | { type: ACTION_TYPE.ADD_TRANSACTION; payload: TInventoryTransaction }
  | { type: ACTION_TYPE.UPDATE_TRANSACTION; payload: TInventoryTransaction }
  | { type: ACTION_TYPE.DELETE_TRANSACTION; payload: string }
  | { type: ACTION_TYPE.ADD_CATEGORY; payload: TInventoryCategory }
  | { type: ACTION_TYPE.UPDATE_CATEGORY; payload: TInventoryCategory }
  | { type: ACTION_TYPE.DELETE_CATEGORY; payload: string }
  | { type: ACTION_TYPE.SET_SEARCH_TERM; payload: string }
  | { type: ACTION_TYPE.SET_CATEGORY_FILTER; payload: string }
  | { type: ACTION_TYPE.FILTER_ITEMS; payload: TInventoryItem[] }
  | { type: ACTION_TYPE.SET_LOADING; payload: boolean }
  | { type: ACTION_TYPE.SET_ERROR; payload: string | undefined }
  | { type: ACTION_TYPE.SET_EDIT_MODE; payload: TEditMode | undefined }
  | { type: ACTION_TYPE.SET_VIEW_STATE; payload: EViewState }
  | { type: ACTION_TYPE.SET_VIEW_TYPE; payload: TInventoryViewType | undefined }
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.SET_MODAL_OPEN; payload: boolean };

const initialState: TInventoryState = {
  items: [],
  transactions: [],
  categories: [],
  selectedItem: undefined,
  selectedTransaction: undefined,
  selectedCategory: undefined,
  filteredItems: [],
  searchTerm: '',
  selectedCategoryFilter: '',
  isLoading: false,
  errorMessage: undefined,
  editMode: undefined,
  viewState: EViewState.LIST,
  currentViewType: undefined,
  paginationParams: {
    page: 1,
    limit: 10,
  },
  itemsCount: 0,
  transactionsCount: 0,
  categoriesCount: 0,
  isModalOpen: false,
};

const InventoryContext = createContext<TInventoryState | undefined>(undefined);
const InventoryDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const inventoryReducer = (
  state: TInventoryState,
  action: Action
): TInventoryState => {
  switch (action.type) {
    case ACTION_TYPE.SET_ITEMS:
      return {
        ...state,
        items: action.payload.data,
        filteredItems: action.payload.data,
        itemsCount: action.payload.total,
      };
    case ACTION_TYPE.SET_TRANSACTIONS:
      return {
        ...state,
        transactions: action.payload.data,
        transactionsCount: action.payload.total,
      };
    case ACTION_TYPE.SET_CATEGORIES:
      return {
        ...state,
        categories: action.payload.data,
        categoriesCount: action.payload.total,
      };
    case ACTION_TYPE.SELECT_ITEM:
      return { ...state, selectedItem: action.payload };
    case ACTION_TYPE.SELECT_TRANSACTION:
      return { ...state, selectedTransaction: action.payload };
    case ACTION_TYPE.SELECT_CATEGORY:
      return { ...state, selectedCategory: action.payload };
    case ACTION_TYPE.ADD_ITEM:
      return {
        ...state,
        items: [...state.items, action.payload],
        filteredItems: [...state.filteredItems, action.payload],
      };
    case ACTION_TYPE.UPDATE_ITEM:
      return {
        ...state,
        items: state.items.map((item) =>
          item.id === action.payload.id ? action.payload : item
        ),
        filteredItems: state.filteredItems.map((item) =>
          item.id === action.payload.id ? action.payload : item
        ),
      };
    case ACTION_TYPE.DELETE_ITEM:
      return {
        ...state,
        items: state.items.filter((item) => item.id !== action.payload),
        filteredItems: state.filteredItems.filter(
          (item) => item.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_TRANSACTION:
      return {
        ...state,
        transactions: [...state.transactions, action.payload],
      };
    case ACTION_TYPE.UPDATE_TRANSACTION:
      return {
        ...state,
        transactions: state.transactions.map((transaction) =>
          transaction.id === action.payload.id ? action.payload : transaction
        ),
      };
    case ACTION_TYPE.DELETE_TRANSACTION:
      return {
        ...state,
        transactions: state.transactions.filter(
          (transaction) => transaction.id !== action.payload
        ),
      };
    case ACTION_TYPE.ADD_CATEGORY:
      return { ...state, categories: [...state.categories, action.payload] };
    case ACTION_TYPE.UPDATE_CATEGORY:
      return {
        ...state,
        categories: state.categories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        ),
      };
    case ACTION_TYPE.DELETE_CATEGORY:
      return {
        ...state,
        categories: state.categories.filter(
          (category) => category.id !== action.payload
        ),
      };
    case ACTION_TYPE.SET_SEARCH_TERM:
      return { ...state, searchTerm: action.payload };
    case ACTION_TYPE.SET_CATEGORY_FILTER:
      return { ...state, selectedCategoryFilter: action.payload };
    case ACTION_TYPE.FILTER_ITEMS:
      return { ...state, filteredItems: action.payload };
    case ACTION_TYPE.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTION_TYPE.SET_ERROR:
      return { ...state, errorMessage: action.payload };
    case ACTION_TYPE.SET_EDIT_MODE:
      return { ...state, editMode: action.payload };
    case ACTION_TYPE.SET_VIEW_STATE:
      return { ...state, viewState: action.payload };
    case ACTION_TYPE.SET_VIEW_TYPE:
      return { ...state, currentViewType: action.payload };
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return { ...state, paginationParams: action.payload };
    case ACTION_TYPE.SET_MODAL_OPEN:
      return { ...state, isModalOpen: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const InventoryProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(inventoryReducer, initialState);

  // Default params for initial fetch
  const itemParams = useMemo(
    () => ({
      page: 1,
      limit: 20,
      ...(state.selectedCategoryFilter && {
        category: state.selectedCategoryFilter as any,
      }),
      ...(state.searchTerm && { search: state.searchTerm }),
    }),
    [state.selectedCategoryFilter, state.searchTerm]
  );

  const transactionParams = useMemo(
    () => ({
      page: 1,
      limit: 20,
    }),
    []
  );

  const categoryParams = useMemo(
    () => ({
      page: 1,
      limit: 50,
      isActive: true,
    }),
    []
  );

  // Fetch data
  const {
    data: itemsResponse,
    isLoading: isLoadingItems,
    isError: isItemsError,
    error: itemsError,
  } = useInventoryItemListHook(itemParams);

  const {
    data: transactionsResponse,
    isLoading: isLoadingTransactions,
    isError: isTransactionsError,
    error: transactionsError,
  } = useInventoryTransactionListHook(transactionParams);

  const {
    data: categoriesResponse,
    isLoading: isLoadingCategories,
    isError: isCategoriesError,
    error: categoriesError,
  } = useInventoryCategoryListHook(categoryParams);

  // Sync state with API responses
  useEffect(() => {
    if (itemsResponse) {
      dispatch({ type: ACTION_TYPE.SET_ITEMS, payload: itemsResponse });
    }
  }, [itemsResponse]);

  useEffect(() => {
    if (transactionsResponse) {
      dispatch({
        type: ACTION_TYPE.SET_TRANSACTIONS,
        payload: transactionsResponse,
      });
    }
  }, [transactionsResponse]);

  useEffect(() => {
    if (categoriesResponse) {
      dispatch({
        type: ACTION_TYPE.SET_CATEGORIES,
        payload: categoriesResponse,
      });
    }
  }, [categoriesResponse]);

  // Handle loading state
  useEffect(() => {
    const isLoading =
      isLoadingItems || isLoadingTransactions || isLoadingCategories;
    dispatch({ type: ACTION_TYPE.SET_LOADING, payload: isLoading });
  }, [isLoadingItems, isLoadingTransactions, isLoadingCategories]);

  // Handle errors
  useEffect(() => {
    if (isItemsError) {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload:
          (itemsError as any)?.response?.data?.message ??
          ERROR_MESSAGE.GENERIC_ERROR,
      });
    } else if (isTransactionsError) {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload:
          (transactionsError as any)?.response?.data?.message ??
          ERROR_MESSAGE.GENERIC_ERROR,
      });
    } else if (isCategoriesError) {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload:
          (categoriesError as any)?.response?.data?.message ??
          ERROR_MESSAGE.GENERIC_ERROR,
      });
    }
  }, [
    isItemsError,
    isTransactionsError,
    isCategoriesError,
    itemsError,
    transactionsError,
    categoriesError,
  ]);

  const contextValue = useMemo(() => state, [state]);

  return (
    <Box
      data-testid="inventory-provider"
      sx={{ width: '100%', height: '100%', overflow: 'hidden' }}
    >
      <InventoryContext.Provider value={contextValue}>
        <InventoryDispatchContext.Provider value={dispatch}>
          {children}
        </InventoryDispatchContext.Provider>
      </InventoryContext.Provider>
    </Box>
  );
};

export const useInventoryState = () => {
  const context = useContext(InventoryContext);
  if (context === undefined) {
    throw new Error(
      'useInventoryState must be used within an InventoryProvider'
    );
  }
  return context;
};

export const useInventoryDispatch = () => {
  const context = useContext(InventoryDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useInventoryDispatch must be used within an InventoryProvider'
    );
  }
  return context;
};

export const useInventoryActions = () => {
  const dispatch = useContext(InventoryDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useInventoryActions must be used within an InventoryProvider'
    );
  }

  const setItems = (itemsResponse: TInventoryItemListApiResponse) => {
    dispatch({ type: ACTION_TYPE.SET_ITEMS, payload: itemsResponse });
  };

  const setTransactions = (
    transactionsResponse: TInventoryTransactionListApiResponse
  ) => {
    dispatch({
      type: ACTION_TYPE.SET_TRANSACTIONS,
      payload: transactionsResponse,
    });
  };

  const setCategories = (
    categoriesResponse: TInventoryCategoryListApiResponse
  ) => {
    dispatch({ type: ACTION_TYPE.SET_CATEGORIES, payload: categoriesResponse });
  };

  const selectItem = (item: TInventoryItem | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_ITEM, payload: item });
  };

  const selectTransaction = (
    transaction: TInventoryTransaction | undefined
  ) => {
    dispatch({ type: ACTION_TYPE.SELECT_TRANSACTION, payload: transaction });
  };

  const selectCategory = (category: TInventoryCategory | undefined) => {
    dispatch({ type: ACTION_TYPE.SELECT_CATEGORY, payload: category });
  };

  const addItem = (item: TInventoryItem) => {
    dispatch({ type: ACTION_TYPE.ADD_ITEM, payload: item });
  };

  const updateItem = (item: TInventoryItem) => {
    dispatch({ type: ACTION_TYPE.UPDATE_ITEM, payload: item });
  };

  const deleteItem = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_ITEM, payload: id });
  };

  const addTransaction = (transaction: TInventoryTransaction) => {
    dispatch({ type: ACTION_TYPE.ADD_TRANSACTION, payload: transaction });
  };

  const updateTransaction = (transaction: TInventoryTransaction) => {
    dispatch({ type: ACTION_TYPE.UPDATE_TRANSACTION, payload: transaction });
  };

  const deleteTransaction = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_TRANSACTION, payload: id });
  };

  const addCategory = (category: TInventoryCategory) => {
    dispatch({ type: ACTION_TYPE.ADD_CATEGORY, payload: category });
  };

  const updateCategory = (category: TInventoryCategory) => {
    dispatch({ type: ACTION_TYPE.UPDATE_CATEGORY, payload: category });
  };

  const deleteCategory = (id: string) => {
    dispatch({ type: ACTION_TYPE.DELETE_CATEGORY, payload: id });
  };

  const setSearchTerm = (term: string) => {
    dispatch({ type: ACTION_TYPE.SET_SEARCH_TERM, payload: term });
  };

  const setCategoryFilter = (category: string) => {
    dispatch({ type: ACTION_TYPE.SET_CATEGORY_FILTER, payload: category });
  };

  const filterItems = (items: TInventoryItem[]) => {
    dispatch({ type: ACTION_TYPE.FILTER_ITEMS, payload: items });
  };

  const setLoading = (isLoading: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_LOADING, payload: isLoading });
  };

  const setError = (errorMessage: string | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_ERROR, payload: errorMessage });
  };

  const setEditMode = (editMode: TEditMode | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_EDIT_MODE, payload: editMode });
  };

  const setViewState = (viewState: EViewState) => {
    dispatch({ type: ACTION_TYPE.SET_VIEW_STATE, payload: viewState });
  };

  const setViewType = (viewType: TInventoryViewType | undefined) => {
    dispatch({ type: ACTION_TYPE.SET_VIEW_TYPE, payload: viewType });
  };

  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  const setModalOpen = (isOpen: boolean) => {
    dispatch({ type: ACTION_TYPE.SET_MODAL_OPEN, payload: isOpen });
  };

  return {
    setItems,
    setTransactions,
    setCategories,
    selectItem,
    selectTransaction,
    selectCategory,
    addItem,
    updateItem,
    deleteItem,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    addCategory,
    updateCategory,
    deleteCategory,
    setSearchTerm,
    setCategoryFilter,
    filterItems,
    setLoading,
    setError,
    setEditMode,
    setViewState,
    setViewType,
    updatePaginationParams,
    setModalOpen,
  };
};
